// Chat DAG — type definitions.
//
// Data shapes carried on each ReactFlow node. Kept small + serializable
// so nodes can be diffed cheaply between turns. New node types land by
// adding a discriminant + a renderer in nodes/index.ts — no runtime
// registry, just TS.

import type { ToolCallEntry } from '../ToolCallPill';
import type { DagArtifact } from '../../../types/dag';
import type { ResolvedHillTarget } from '../../../types/hill';
import type { Artifact } from '../../../types/artifact';
import type { CostTelemetry } from '../../../types/council';

export type ChatDagNodeType =
  | 'turn_marker'
  | 'llm_round'
  | 'tool_call'
  | 'peer'
  | 'fan_collapsed_chip'
  | 'phase_stack_group'
  | 'collapsed_phase_stack'
  | 'turn_separator'
  | 'artifact_tile'
  | 'orphan_tray'
  // v0.2 Council Trail peer-spine refactor — new node kinds:
  | 'spine_root_marker'
  | 'peer_response'
  | 'spine_fan_chip'
  | 'cycle_error'
  // v0.2.x — chrome chip pinned above the spine root of any trail.
  | 'trail_head_marker'
  // Squad fold-back SP-1: convergence node for a completed squad synth.
  | 'squad_merge'
  // ChatDAGRAGDAG C2.2: host-authored merge-back of a Camp into the main spine.
  | 'merge_node'
  // Trailmap L2 Drip 2 — Pass-2 cluster bounding-box decoration (AABB behind tiles).
  | 'cluster_aabb'
  // Living Blueprint SP1 — summoned project_manifest as a live flip-card decoration.
  | 'artifactory_blueprint'
  // Source Spines SP-2 — dormant stem hung below a collapsed blueprint card.
  | 'spine_lane_stem'
  // Source Spines SP-3a — the composite intent-chat conversation panel
  // (replaces SP-2's per-reply cascade). One node per blueprint_intent spine.
  | 'spine_chat_panel';

// v0.3 runtime state for nodes driven by live WS phase events
// (Tasks 7-12). Absence is equivalent to 'completed' for historical
// (non-live) nodes.
export type RunState =
  | 'completed'
  | 'running'
  | 'error'
  | 'timeout'
  | 'rate_limited'
  | 'budget_exceeded'
  | 'deduped'
  | 'cached';

// Fields common to every node data payload. turnIndex + isLatestTurn
// are optional during the v0.2 → v0.3 transition because the v0.2
// processor does not populate them; the v0.3 processor (Task 5) always
// populates them, so renderers can treat them as "defined when flag on".
interface BaseNodeData {
  turnIndex?: number;
  isLatestTurn?: boolean;
  reconsideredOrigin?: boolean; // C2.3 — a council_switchback re-blazed from this turn (⤺ marker)
  messageId?: string;          // spine-binding target (own msg id)
  // v0.3 Task 10: the user message id that opens the turn this node
  // belongs to — stable across speculative + canonical emission. Used
  // by inFlightTurnId / viewportTurnId matching for opacity priority
  // and by identity-preserving reconcile. For user markers, equals
  // messageId. For everything else, equals the preceding user msg id.
  turnKey?: string;
  // P3 SP2 — the message's metadata.trail_id (string uuid). null = legacy
  // pre-minting message; layout-synthesized nodes never set it.
  trailId?: string | null;
}

export interface TurnMarkerData extends BaseNodeData {
  kind: 'turn_marker';
  role: 'user' | 'assistant';
  preview: string;        // first ~80 chars of message content
  messageId: string;      // always present for markers
  timestamp?: string;
  // Pass-through for surface-level turn telemetry so the node can
  // render a cost chip etc. without having to re-walk messages.
  totalCostUsd?: number | null;
  totalTokens?: number | null;
  toolCallCount?: number;
  // Trailmap L2 Drip 1 — artifact disclosure (stamped by the overlay).
  // artifactCount = resolvable artifacts bound to this turn; artifactTurnKey
  // = source_turn_id the badge toggles; artifactsExpanded = tiles revealed.
  artifactCount?: number;
  artifactTurnKey?: string;
  artifactsExpanded?: boolean;
  // Full text of the message — surfaced on hover. `preview` remains
  // the 80-char in-node summary; `fullContent` powers the hovercard
  // so the user can read the whole prompt / answer without opening
  // the bubble.
  fullContent?: string;
  // Model used for the assistant turn (from message.metadata.model).
  modelUsed?: string;
  // v0.4 Task 6: primary persona object for this chat session.
  // Threaded onto assistant turn markers only — user markers don't
  // need it. Caller plumbs via messagesToDag runtime.primaryPersona.
  primaryPersona?: any;
}

export interface LlmRoundData extends BaseNodeData {
  kind: 'llm_round';
  turnId: string;         // message id
  round: number;          // 1-indexed
  modelUsed?: string;
  isSynthesis: boolean;   // ② — true only for the LAST round of a MULTI-round turn (a real synthesis)
  isLoneAnswer?: boolean; // ② — true for the only round of a single-round turn (the "Answer" round; pure-solo turns emit no round at all)
  toolCallCount: number;  // how many tool calls fanned out from this round
  runState?: RunState;    // populated in Tasks 7 / 11-12; absent = completed
  // Synthesis-only context — turn-level totals + answer preview pulled
  // from the asst message metadata when this is the LAST round of the
  // turn. Lets the synthesis node render as a "delivery card" without
  // forcing the user to drop down to the assistant marker just to see
  // tokens / cost / what was said. Only populated for synthesis rounds
  // of completed turns; speculative + non-synth rounds leave these undefined.
  turnTotalTokens?: number | null;
  turnTotalCostUsd?: number | null;
  turnDurationMs?: number | null;
  turnAnswerPreview?: string;
  turnTimestamp?: string;
  // Non-synth rounds: which tools fanned out from this round, in
  // call order. Drives the at-a-glance "→ search · roster · …" line.
  // Synth rounds usually have toolCallCount=0 and leave this empty.
  toolNames?: string[];
}

export interface ToolCallDagData extends BaseNodeData {
  kind: 'tool_call';
  call: ToolCallEntry;
  messageId: string;
  round: number;
  runState?: RunState;    // populated in Tasks 8 / 11-12; absent = completed
}

export interface PeerDagData extends BaseNodeData {
  kind: 'peer';
  personaName?: string;
  personaId?: string;
  status?: string;        // ok | timeout | error | offline
  durationMs?: number;
  response?: string;
  parentToolCallId?: string;
  runState?: RunState;    // populated in Tasks 8 / 11-12; absent = completed
  // Enrichment for the at-a-glance card + hovercard — threaded
  // through from the parent consult_peers call.
  // `prompt` + `context` are the args passed to consult_peers
  // (shared across all peers of the same call); the peer actually
  // received `context + "\n\n" + prompt` as its user_message.
  prompt?: string;
  context?: string;
  model?: string;
  error?: string;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
  costUsd?: number;
  // Per-peer match info from consult_peers' fuzzy resolver. Surfaces
  // "why this specific peer was picked" — useful when the LLM passes
  // free-text queries instead of UUIDs.
  matchedVia?: string;          // tag | name_exact | name_substring | uuid_exact | ...
  matchedTag?: string | null;
  matchScore?: number;
  candidateCount?: number;
  matchQueries?: string[];      // raw queries that resolved to this peer (deduped)
}

// v0.3 density control — collapsed peer fans show a single chip with
// aggregate counts (peer_count, ok, offline, error). Click expands the
// fan via ChatDagRuntimeContext.collapsedFanIds (Task 6).
export interface FanCollapsedChipData extends BaseNodeData {
  kind: 'fan_collapsed_chip';
  parentToolCallId: string;
  peerCount: number;
  okCount: number;
  offlineCount: number;
  errorCount: number;
  totalDurationMs: number | null;
  totalTokens: number | null;
  messageId?: string;           // set from the parent turn; used by spine
}

// "Phase stack" bounding box around ALL of a turn's rounds + their
// tool calls + peer/chip fans — the complete sequence from user
// prompt to synthesized answer. One box per turn. Emitted by the
// layout post-pass after all child positions are computed; rendered
// BEHIND the stack's contents (zIndex: -1) as a transparent/tinted
// rounded rect. Outline highlights when the turn is in flight,
// errored, or matches viewportTurnId. Carries an auto-derived name
// (first ~40 chars of the user prompt) so the controls panel can
// list stacks by name.
export interface PhaseStackGroupData extends BaseNodeData {
  kind: 'phase_stack_group';
  turnKey: string;
  turnIndex: number;
  name: string;
  roundCount: number;
  toolCount: number;
  childCount: number;
}

// Compact placeholder card emitted in place of a turn's full content
// when the user has collapsed that phase stack via the controls panel.
// Hangs from the user marker and feeds into the assistant marker;
// rounds / tools / peers are NOT emitted while this card is present.
export interface CollapsedPhaseStackData extends BaseNodeData {
  kind: 'collapsed_phase_stack';
  turnKey: string;
  turnIndex: number;
  name: string;
  roundCount: number;
  toolCount: number;
  peerCount: number;
  totalCostUsd: number | null;
  totalTokens: number | null;
}

// Horizontal trail separator — emitted by the layout post-pass
// between adjacent turns. Renders as a centered horizontal rule
// flanked by a small "Turn N" label, providing a visible chapter
// break along the ChatTrail's vertical spine.
export interface TurnSeparatorData extends BaseNodeData {
  kind: 'turn_separator';
  turnIndex: number;   // index of the NEXT turn (the one beginning here)
  label: string;       // pre-formatted, e.g. "Turn 3" or "Turn 3 · 14:32"
  timestamp?: string;  // user marker's timestamp (next-turn start), if known
}

// Trailmap L2 Drip 2 — the grouping dimension. Internal id `off` = the
// flat (Drip 1) view; user-facing label for `phase` is "round".
export type GroupingDim = 'off' | 'turn' | 'phase' | 'persona';

export interface ClusterBBox { x: number; y: number; w: number; h: number; }

// Data for a cluster_aabb decoration node — a bounding box rendered BEHIND a
// cluster's tiles with a header. `value` is the normalized cluster key (null =
// the catch-all: no round / unknown persona). `label` is optional; the node
// resolves a friendly label from dim+value at render (usePersonaName etc).
export interface ClusterAabbData {
  kind: 'cluster_aabb';
  dim: GroupingDim;
  value: string | null;
  count: number;
  bbox: ClusterBBox;
  label?: string;
  soloOfDim?: boolean; // true when this is the only cluster in the dim
}

// Trailmap L1 (plan Task 12) — artifact tile bound to source_turn_id.
// `onClick` is the canvas-level handler that toggles in-canvas expansion
// (Drip 3): fetches via getArtifact and blows the tile open to a
// content-natural card in place — superseding the L1 corner ArtifactViewer
// panel (DC-10).
export interface ArtifactTileData extends BaseNodeData {
  kind: 'artifact_tile';
  artifact: DagArtifact;
  onClick: (artifactId: string) => void;
  // Trailmap L2 Drip 3 — expansion state, processor-computed.
  expanded: boolean;
  content?: Artifact;        // full body (content map status==='ok')
  contentError?: boolean;    // fetch failed (status==='error')
  // SP-3b — "Ask" affordance (chat-with-artifact). Node receives typed callbacks
  // from the canvas (never touches runtime). Wired via the RuntimeState channel.
  onAsk?: (artifactId: string) => void;
  askInFlight?: boolean;
  askError?: string | null;
  // SP-3b — F5 survival: a tile whose spine has operator turns default-expands
  // once on mount. onSeedSpineExpanded is the typed seeder (= runtime.seedCardSpineExpanded).
  hasOperatorTurns?: boolean;
  onSeedSpineExpanded?: (cardId: string) => void;
  // SP-3c — spine-lane collapse state, stamped by promoteCardLane (the only
  // writer). undefined = no spine (renders "Ask"); boolean = has a spine (renders
  // the ⌄/▸ Spine chevron). NOT `expanded` (content-viewer state — name-trap).
  spineExpanded?: boolean;
  // SP-3c — collapse/expand the artifact's spine lane. Typed prop (= runtime.
  // toggleCardSpineExpansion); the tile NEVER reads runtime directly. Drives the
  // ⌄/▸ Spine chevron. Present only when the tile has a spine (layout stamps
  // data.spineExpanded; see promoteCardLane).
  onToggleSpine?: (cardId: string) => void;
}

// Trailmap L2 Drip 3 — canvas-local per-artifact fetch state (the content
// map's value type, keyed by artifact id). Lives in ChatDagCanvas, off the
// MessageList re-render path. `session` stamps the 'ok' entry so the cache
// skips refetch only on a SAME-SESSION hit (fold §D — guards cross-session bleed).
export type ArtifactContentEntry =
  | { status: 'loading' }
  | { status: 'ok'; artifact: Artifact; session?: string }
  | { status: 'error' };

// Trailmap L1 (plan Task 12) — Orphan Tray (DC3) holds NULL source_turn_id
// artifacts as visible chips at session root. Empty array → render null.
export interface OrphanTrayData extends BaseNodeData {
  kind: 'orphan_tray';
  artifacts: DagArtifact[];
  onClick: (artifactId: string) => void;
}

// v0.2 — entry chip for a peer persona's chain. Renders compact: small
// color dot + persona name + tiny "▸" pip indicating chain starts here.
// No verb chrome (cartographic metaphor, not meeting-room).
export interface SpineRootMarkerData extends BaseNodeData {
  kind: 'spine_root_marker';
  personaId: string;          // renderer late-binds name + color via hooks
  invitedAt?: string;
  spineRootMessageId: string; // own message id
  task?: string;              // C2.1 sp4 — the brief the peer was invited to do
  forkOriginId?: string;      // C2.1 sp4 — mirror of parent_ids[0] (the fork-origin turn)
  arrivalState?: 'pending' | 'resolved' | 'failed' | 'standby' | 'merged';  // C2.1 sp5 + C2.2 merged
  turnFailedReason?: string;                          // C2.1 sp5 — set when failed
  resolvedTarget?: ResolvedHillTarget;                // SP3a Hills — drive-rule output for the chip
  spawnSource?: string;       // SP2a/SP-3b — 'blueprint_intent' | 'artifact' marks a source-card spine root
  sourceManifestId?: string;  // SP2a — the manifest whose intent this spine hosts
  sourceArtifactId?: string;  // SP-3b — the artifact this spine hosts (spawn_source_id, artifact kind)
  spineRole?: string;         // SP-2 — 'card-lane-head' renders the root as the card's bottom band
}

// Source Spines SP-2 — the dormant stem that hangs below a COLLAPSED blueprint
// card (a short amber rail signalling "a spine is tucked here"). Layout-only;
// emitted by positionIntentSpine when the card is not in expandedCardSpineIds.
export interface SpineLaneStemData extends BaseNodeData {
  kind: 'spine_lane_stem';
  cardId: string;
}

// Source Spines SP-3a — one ordered conversation turn in the intent panel.
// Defined here (not utils/intentThread) so the node-data type can reference it
// without a util import cycle; buildIntentThread imports it from here.
export interface IntentTurn {
  messageId: string;
  role: 'user' | 'foreman';
  content: string;
  personaId: string | null;
  timestamp: any;
}

// Source Spines SP-3a — the composite intent-chat panel. A dumb renderer:
// the processor pre-builds `thread` (user + foreman turns, ordered), the panel
// reuses ConversationContext.sendSpineReply + pendingReplies for live state.
// Carries ZERO canvas edges (positioned below the card; collapse = dormant stem).
export interface SpineChatPanelData extends BaseNodeData {
  kind: 'spine_chat_panel';
  cardId: string;
  spineRootMessageId: string;
  thread: IntentTurn[];
  personaId: string | null;
  residentNamesByPersona: Record<string, string | null>;
  hasOperatorTurns: boolean;
  artifactName?: string;            // SP-3b — artifact spine cold-start header (C5)
  artifactType?: string;            // SP-3b
  hostPersonaName?: string | null;  // SP-3b — filled by the panel from context (C5)
}

// v0.2 — peer-authored assistant message. Renders full content with
// persona-tinted bubble + persona-named header + telemetry chip.
// Mirrors turn_marker[role='assistant'] data shape for renderer reuse.
export interface PeerResponseData extends BaseNodeData {
  kind: 'peer_response';
  personaId: string;          // renderer late-binds via hooks
  content: string;
  preview: string;
  fullContent: string;
  timestamp?: string;
  spineRootMessageId?: string;
  modelUsed?: string;
  totalCostUsd?: number | null;
  costTelemetry?: CostTelemetry | null;  // G4b — honest est/actual divergence from msg.metadata.cost_telemetry
  totalTokens?: number | null;
  toolCallCount?: number;
  squadId?: string;          // SP-1 squad-fan detection (A7) + provenance
  stance?: string;           // SP-3: 'neutral' | 'skeptical' | 'allegiant'
  chorusStance?: { angle: string; diverge_toward?: string | null; promoted_trail_root?: string };  // TH-4 — read's declared stance (isChorus fan + dissent chip)
  resolvedTarget?: ResolvedHillTarget;  // SP3a Hills — drive-rule output for the chip
  spineSpawnSource?: string;  // Source Spines SP-2 — stamped when this reply descends from a blueprint_intent spine root (discriminates from squad peer_response)
  laneIndex?: number;         // Source Spines SP-2 — arrival order within the expanded spine lane (cascade stagger); stamped by positionIntentSpine
}

// Squad fold-back SP-1: the convergence node for a completed squad. SP-1
// ships prose + citations + inlet counts; SP-3 adds status/stance chrome.
export interface SquadMergeData extends BaseNodeData {
  kind: 'squad_merge';
  squadId: string;
  synthPreview: string;
  fullContent: string;
  memberCount: number;
  reportedCount: number;
  citations: Array<{ personaId: string; spineRootMessageId: string; stance?: string }>;
  failedCount: number;       // SP-3: memberCount - reportedCount
}

// ChatDAGRAGDAG C2.2 — the convergence node for a host-authored Camp merge-back.
// Mirrors SquadMergeData's prose+provenance shape; provenance is the Camp(s)
// folded in (merged_from), late-binding persona names via hooks at render.
export interface MergeNodeData extends BaseNodeData {
  kind: 'merge_node';
  synthPreview: string;
  fullContent: string;
  mergedFrom: Array<{ campId: string; personaId: string | null; branchTailId: string }>;
  costTelemetry?: CostTelemetry | null;  // G4b — divergence from msg.metadata.cost_telemetry
}

// v0.2 — progressive-disclosure chip for N≥3 sibling spine fans.
// Auto-collapses peer_response siblings when the operator hasn't yet
// expanded the fork; click flips ChatDagRuntimeContext.collapsedFanIds
// via the shared toggleFanExpansion(parentFanKey) helper. Deterministic
// parentFanKey: `${parent.id}-fanchip`.
export interface SpineFanChipData extends BaseNodeData {
  kind: 'spine_fan_chip';
  parentMessageId: string;
  parentFanKey: string;       // `${parentMessageId}-fanchip` — deterministic
  childCount: number;
  personaIds: string[];
  // SP3-MF-06 — flavor polymorphism. Existing fans (proposal approvals)
  // back-fill to 'proposals' in topologyWalk.synthesizeSpineFanChip;
  // ChatDAGRAGDAG squad-context fans set 'squad' + squadId so the chip
  // label reads "N squad members" instead of "N proposals approved here".
  flavor?: 'proposals' | 'squad' | 'generic' | 'chorus';
  squadId?: string;           // populated when flavor === 'squad'
}

// v0.2.x — synthetic chrome chip pinned ABOVE the spine root of a trail.
// Emitted by decorationPass after Pass 1; sits at (root.x, root.y - GAP)
// so the trail visually starts with a "🥾 Trail head" badge regardless
// of whether the root node is a user turn_marker, an orphan-asst
// llm_round (persona seed content), or a spine_root_marker (council
// peer chain).
//
// Decoration only — does NOT participate in the spine topology;
// has no edges; layout positions it relative to the root.
export interface TrailHeadMarkerData extends BaseNodeData {
  kind: 'trail_head_marker';
  rootNodeId: string;       // id of the spine root this marker pins above
  rootNodeKind: ChatDagNodeType;
}

// v0.2 — emitted by topologyWalk when MAX_DEPTH cycle bailout fires.
// Rose-bordered chip rendered AT the bailout (x,y) — NOT at (0,0) —
// so the operator sees where the cycle was detected. Auto-clears on
// next render (no persistence); regenerated each layoutDag call.
export interface CycleErrorData extends BaseNodeData {
  kind: 'cycle_error';
  parentId: string;
  position: { x: number; y: number };
}

// Living Blueprint SP1 — a summoned project_manifest rendered as a live
// flip-card decoration node. manifestId drives the in-card manifest fetch;
// onOpen routes to the full Artifactory view.
export interface BlueprintNodeData extends BaseNodeData {
  kind: 'artifactory_blueprint';
  manifestId: string;
  onOpen?: (manifestId: string) => void;
  // SP2b — open the editable genesis cost-gate for this blueprint (seedling
  // "Generate" + failure "Retry" both route here). Wired through the runtime.
  onGenerate?: (manifestId: string, intent: string) => void;
  // SP-3a — true when this card's intent spine carries operator reply turns;
  // stamped by the processor (Task 5) and read by BlueprintNode to default the
  // spine expanded after F5 (so the conversation isn't hidden on reload).
  hasOperatorTurns?: boolean;
}

export type ChatDagNodeData =
  | TurnMarkerData
  | LlmRoundData
  | ToolCallDagData
  | PeerDagData
  | FanCollapsedChipData
  | PhaseStackGroupData
  | CollapsedPhaseStackData
  | TurnSeparatorData
  | ArtifactTileData
  | OrphanTrayData
  // v0.2 Council Trail peer-spine refactor:
  | SpineRootMarkerData
  | PeerResponseData
  | SquadMergeData
  | MergeNodeData
  | SpineFanChipData
  | CycleErrorData
  | TrailHeadMarkerData
  // Living Blueprint SP1:
  | BlueprintNodeData
  // Source Spines SP-2:
  | SpineLaneStemData
  // Source Spines SP-3a:
  | SpineChatPanelData;

// v0.2 — single source of truth for the four-bucket layout axis.
// Walker (topologyWalk.ts) and Pass 2 (layoutGroups.ts) import from
// this Record. Adding a new kind triggers a TS exhaustiveness error
// here and at every consumer until populated. This is the enforcement
// point for the four-bucket axis — do NOT inline-filter by kind in
// dispatch sites; always look up here.
export type NodeGroup = 'ANCHORS' | 'SPINE' | 'LATERAL' | 'DECORATION';

export const NODE_GROUP: Record<ChatDagNodeType, NodeGroup> = {
  // ANCHORS — top-of-trail entry points; stable per-persona column
  spine_root_marker: 'ANCHORS',

  // SPINE — vertical, stack in column at parent's x
  turn_marker: 'SPINE',
  llm_round: 'SPINE',
  peer_response: 'SPINE',
  // SP-1 — single-parent child of the anchor (native layout, no fan-in).
  squad_merge: 'SPINE',
  // C2.2 — converges on the main spine; positioned by repositionMergeNodes.
  merge_node: 'SPINE',
  spine_fan_chip: 'SPINE',
  collapsed_phase_stack: 'SPINE',

  // LATERAL — offset to parent's right
  tool_call: 'LATERAL',
  peer: 'LATERAL',
  fan_collapsed_chip: 'LATERAL',

  // DECORATION — Pass 2, content-relative; never anchor
  phase_stack_group: 'DECORATION',
  turn_separator: 'DECORATION',
  artifact_tile: 'DECORATION',
  orphan_tray: 'DECORATION',
  cycle_error: 'DECORATION',
  trail_head_marker: 'DECORATION',
  // Trailmap L2 Drip 2 — paint-behind cluster box (same group as phase_stack_group).
  cluster_aabb: 'DECORATION',
  // Living Blueprint SP1 — trail-level flip card, positioned in decorationPass.
  artifactory_blueprint: 'DECORATION',
  // Source Spines SP-2 — dormant stem under a collapsed card; content-relative.
  spine_lane_stem: 'DECORATION',
  // Source Spines SP-3a — the intent-chat panel sits in the SPINE band below
  // its card (positioned by positionIntentSpine, carries zero edges).
  spine_chat_panel: 'SPINE',
};
